/**
 * ProjectNumber — small bordered monospace chip for displaying project
 * numbers consistently across the app.
 *
 * Per UX feedback (Patch P2): project number is the identity anchor of a
 * project (used as a click target on the Kanban card, the detail header,
 * search results, etc.) and benefits from a consistent visual chip
 * treatment so the eye reliably lands on it.
 *
 * Tone is neutral by default — the chip is identity, not status. Use
 * StatusBadge for lifecycle / workflow state alongside this.
 */
import type { HTMLAttributes } from 'react';
import { cn } from '@/lib/cn';

export interface ProjectNumberProps extends HTMLAttributes<HTMLSpanElement> {
  number: string;
  /**
   * Size variants:
   *   - 'sm': compact, suitable for table rows and dense card grids
   *   - 'md': default, suitable for page headers and breadcrumbs
   */
  size?: 'sm' | 'md';
}

export function ProjectNumber({
  number,
  size = 'md',
  className,
  ...rest
}: ProjectNumberProps) {
  return (
    <span
      className={cn(
        'inline-flex items-center rounded-sm border border-border-default bg-subtle font-mono font-medium tabular-nums text-secondary',
        size === 'sm' ? 'px-1.5 py-0 text-[11px]' : 'px-2 py-0.5 text-xs',
        className,
      )}
      {...rest}
    >
      {number}
    </span>
  );
}
